import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  InteractionStatus,
  EventType,
  EventMessage,
  AccountInfo,
  AuthError,
} from '@azure/msal-browser';
import { AngularFaviconService } from 'angular-favicon';
import { PrimeNG } from 'primeng/config';
import { Subject, takeUntil, filter } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CRM_B2C_FORGOT_PASSWORD_RECOVERY_SESSION_KEY } from './app-config';
import { MsalRedirectService } from './auth/msal-redirect.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private readonly destroy$ = new Subject();
  private hasNavigated = false;
  /** Prevents duplicate loginRedirect when B2C returns tokens from the password-reset policy only. */
  private signInAfterPasswordResetStarted = false;

  constructor(
    private router: Router,
    private broadcastService: MsalBroadcastService,
    private authService: MsalService,
    private msalRedirectService: MsalRedirectService,
    private ngxFavicon: AngularFaviconService,
  ) {}

  private readonly primeng = inject(PrimeNG);

  /** B2C may surface forgot-password on errorCode, errorMessage, or message — match all. */
  private static b2cForgotPasswordError(err: AuthError | null | undefined): boolean {
    if (!err) {
      return false;
    }
    const anyErr = err as AuthError & { errorMessage?: string };
    const blob = `${anyErr.errorCode ?? ''} ${anyErr.errorMessage ?? ''} ${anyErr.message ?? ''}`;
    return blob.includes('AADB2C90118');
  }

  ngOnInit() {
    this.ngxFavicon.setFavicon(`assets/${environment.favIcon}`);
    this.primeng.ripple.set(true);

    this.msalRedirectService.handleRedirectOnce().pipe(takeUntil(this.destroy$)).subscribe();
    
    this.authService.instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        this.authService.instance.setActiveAccount(event.payload as AccountInfo);
      }
    });

    this.broadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this.destroy$)
      )
      .subscribe((result: EventMessage) => {
        const err = result.error as AuthError;
        if (AppComponent.b2cForgotPasswordError(err)) {
          // B2C "forgot password" error occurred globally (e.g. while trying to access /crm)
          // Session flag survives if login loads before/with a clean URL; query still helps LoginComponent.
          try {
            sessionStorage.setItem(CRM_B2C_FORGOT_PASSWORD_RECOVERY_SESSION_KEY, '1');
          } catch {
            /* ignore private mode / quota */
          }
          window.location.href = '/?error=AADB2C90118';
        }
      });
    
    this.broadcastService.inProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status: InteractionStatus) => {
        if (status === InteractionStatus.None) {
          setTimeout(() => {
            this.setAccountAndNavigate();
          }, 0);
        }
      });
  }

  private accountFromPasswordResetPolicy(account: AccountInfo | null): boolean {
    if (!account?.idTokenClaims) {
      return false;
    }
    const claims = account.idTokenClaims as Record<string, unknown>;
    const tfp = typeof claims['tfp'] === 'string' ? (claims['tfp'] as string) : '';
    return !!tfp && tfp.toLowerCase() === environment.passwordResetName.toLowerCase();
  }

  private setAccountAndNavigate() {
    const accounts = this.authService.instance.getAllAccounts();

    if (accounts.length > 0) {
      if (!this.authService.instance.getActiveAccount()) {
        this.authService.instance.setActiveAccount(accounts[0]);
      }

      const activeAccount = this.authService.instance.getActiveAccount();
      // Must run even if we already navigated to CRM once in this tab (password reset is a new redirect).
      if (this.accountFromPasswordResetPolicy(activeAccount)) {
        if (!this.signInAfterPasswordResetStarted) {
          this.signInAfterPasswordResetStarted = true;
          // Log out of the password-reset session to clear the B2C session and land back on the app login page
          void this.authService.instance.logoutRedirect({
            authority: environment.passwordResetAuthority,
          });
        }
        return;
      }
      this.signInAfterPasswordResetStarted = false;
    }

    if (this.hasNavigated) {
      return;
    }

    if (accounts.length > 0) {
      const isOnLoginPage = this.isLoginRoute();

      if (isOnLoginPage) {
        this.hasNavigated = true;
        this.navigateFromLoginToCrm();
      }
    }
  }

  private isLoginRoute(): boolean {
    const path = window.location.pathname || '/';
    const onRoot = path === '/' || path === '';
    const routerOnRoot =
      this.router.url === '/' ||
      this.router.url.startsWith('/?') ||
      this.router.url.startsWith('/#');
    return onRoot || routerOnRoot;
  }

  /** Preserve query params only for CRM deep links (`module=`), not OAuth `code`/`state`. */
  private navigateFromLoginToCrm(): void {
    const tree = this.router.parseUrl(this.router.url);
    const mod = tree.queryParams['module'];
    const modStr = mod === undefined || mod === null ? '' : String(Array.isArray(mod) ? mod[0] : mod).trim();
    const hasCrmDeepLink = modStr !== '';
    this.router.navigate(['crm'], hasCrmDeepLink ? { queryParamsHandling: 'preserve' } : {});
  }
}
